/**
 * Чтение состояния софтфона из компонентов.
 *
 * useSyncExternalStore, а не useState + подписка: снимок из ядра неизменяемый и
 * меняет ссылку только при реальном изменении, поэтому React сам решает, что перерисовать,
 * и не теряет обновления между рендерами.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { OutboundCallRequest, SoftphoneSnapshot } from '@cc/softphone-core';

import { useSoftphoneClient } from './provider.js';

export interface SoftphoneApi {
  snapshot: SoftphoneSnapshot;
  /** Звонок по карточке: assignmentId уезжает на станцию SIP-заголовком */
  call: (request: OutboundCallRequest) => Promise<void>;
  hangup: () => Promise<void>;
  setMuted: (value: boolean) => void;
  setInputDevice: (deviceId: string) => Promise<void>;
  setOutputDevice: (deviceId: string) => Promise<void>;
  /** Убрать завершённый звонок с пульта после простановки метки */
  clearCall: () => void;
  /** Можно ли звонить прямо сейчас: линия есть, вкладка лидер, звонок не идёт */
  canCall: boolean;
}

export function useSoftphone(): SoftphoneApi {
  const client = useSoftphoneClient();

  const snapshot = useSyncExternalStore(
    useCallback((listener: () => void) => client.subscribe(listener), [client]),
    useCallback(() => client.getState(), [client]),
    useCallback(() => client.getState(), [client]),
  );

  const call = useCallback(
    async (request: OutboundCallRequest) => {
      await client.call(request);
    },
    [client],
  );

  const activeCall = snapshot.call !== null && snapshot.call.state !== 'ended';

  return {
    snapshot,
    call,
    hangup: useCallback(() => client.hangup(), [client]),
    setMuted: useCallback((value: boolean) => { client.setMuted(value); }, [client]),
    setInputDevice: useCallback((id: string) => client.setInputDevice(id), [client]),
    setOutputDevice: useCallback((id: string) => client.setOutputDevice(id), [client]),
    clearCall: useCallback(() => { client.clearCall(); }, [client]),
    canCall: snapshot.line === 'registered' && snapshot.isLeaderTab && !activeCall,
  };
}
